/**
 * New 命令 - 从一句话生成 PRD 和任务
 * 实现 "一句话到发布" 的愿景
 */

import { Command } from 'commander';
import fs from 'fs-extra';
import path from 'path';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import { PRDGenerator } from '../core/prd-generator';
import { PRDParser } from '../core/prd-parser';
import { TaskStore } from '../core/task-store';
import { colors } from '../utils/colors';
import { Table } from 'console-table-printer';
import { DesignDocManager } from '../core/design-doc-manager';

export function createNewCommand(): Command {
  const command = new Command('new');
  
  command
    .description('Generate PRD and tasks from a single idea')
    .argument('<idea>', 'Your product idea or feature description')
    .option('-t, --template <template>', 'PRD template to use (full/simple/custom)', 'simple')
    .option('-o, --output <dir>', 'Output directory for PRD', '.workflow/prd')
    .option('--auto-create', 'Automatically create tasks without confirmation', false)
    .option('--preview', 'Preview PRD without saving', false)
    .option('--no-tasks', 'Only generate PRD without creating tasks', false)
    .option('--with-design', 'Also generate design document', false)
    .option('--design-template <template>', 'Design document template (full/simple)', 'simple')
    .option('--ai-mode <mode>', 'AI mode for task creation (auto/manual/hybrid/memory-enhanced)', 'auto')
    .option('--enable-memory', 'Enable memory system for intelligent suggestions', false)
    .option('--user-id <id>', 'User ID for memory system')
    .action(withErrorHandling(async (idea: string, options) => {
      console.log(colors.header('\n🚀 Gongfu Workflow - From Idea to PRD\n'));
      console.log(colors.info(`Processing idea: "${idea}"`));
      
      // 获取项目配置
      const configPath = path.join(process.cwd(), '.workflow', 'config', 'project.json');
      let projectId: string | undefined;
      if (await fs.pathExists(configPath)) {
        const config = await fs.readJSON(configPath);
        projectId = config.id;
      }
      
      // 初始化生成器
      const generatorOptions = {
        enableMemory: options.enableMemory,
        userId: options.userId || process.env.USER || 'user',
        projectId: projectId,
      };
      const generator = new PRDGenerator(process.cwd(), generatorOptions);
      const parser = new PRDParser();
      const taskStore = new TaskStore();
      
      // 列出可用模板
      if (options.template === 'list') {
        const templates = await generator.listTemplates();
        console.log(colors.header('\n📄 Available PRD Templates:\n'));
        templates.forEach(t => {
          console.log(`  - ${t.replace('.md', '')}`);
        });
        return;
      }
      
      // 生成 PRD
      console.log(colors.dim('\n🤖 Generating PRD from your idea...'));
      
      const prdResult = await generator.generateFromIdea(idea, {
        template: options.template,
        author: process.env.USER || 'user',
        createTasks: !options.noTasks, // 根据选项决定是否创建任务
        aiMode: options.aiMode,
        userId: options.userId || process.env.USER || 'user',
        projectId: projectId,
      });
      
      // 预览模式
      if (options.preview) {
        console.log(colors.header('\n📄 Generated PRD Preview:\n'));
        console.log(colors.dim('─'.repeat(60)));
        console.log(prdResult.content);
        console.log(colors.dim('─'.repeat(60)));
        return;
      }
      
      // 保存 PRD
      const prdPath = await generator.savePRD(prdResult.id, prdResult.content);
      console.log(colors.success(`\n✅ PRD saved to: ${prdPath}`));
      
      // 显示创建的任务信息
      if (prdResult.tasks && prdResult.tasks.length > 0) {
        console.log(colors.header('\n📊 PRD Analysis:\n'));
        
        const statsTable = new Table({
          columns: [
            { name: 'metric', title: 'Metric', alignment: 'left' },
            { name: 'value', title: 'Value', alignment: 'right' }
          ]
        });
        
        statsTable.addRow({ metric: 'PRD ID', value: prdResult.id });
        statsTable.addRow({ metric: 'Generated Tasks', value: prdResult.tasks.length });
        
        // 计算总估时
        const totalEstimate = prdResult.tasks.reduce((sum, task) => {
          if (task.estimate) {
            const match = task.estimate.match(/(\d+)([hd])/);
            if (match) {
              const value = parseInt(match[1]);
              const unit = match[2];
              return sum + (unit === 'd' ? value * 8 : value);
            }
          }
          return sum;
        }, 0);
        
        statsTable.addRow({ metric: 'Total Estimate', value: `${totalEstimate}h` });
        statsTable.printTable();
        
        // 显示任务列表
        console.log(colors.header('\n📋 Generated Tasks:\n'));
        
        const taskTable = new Table({
          columns: [
            { name: 'id', title: 'ID', alignment: 'center' },
            { name: 'type', title: 'Type', alignment: 'center', colored: true },
            { name: 'content', title: 'Content', alignment: 'left', maxLen: 40 },
            { name: 'priority', title: 'Priority', alignment: 'center', colored: true },
            { name: 'estimate', title: 'Est.', alignment: 'center' },
            { name: 'agent', title: 'Agent', alignment: 'center', colored: true }
          ]
        });
        
        for (const task of prdResult.tasks) {
          taskTable.addRow({
            id: task.id.substring(0, 8),
            type: (task.type || 'task').toUpperCase(),
            content: task.content.length > 40 ? task.content.substring(0, 37) + '...' : task.content,
            priority: task.priority.toUpperCase(),
            estimate: task.estimate || '-',
            agent: task.agentAssignments?.primary || 'unassigned'
          });
        }
        
        taskTable.printTable();
        
        console.log(colors.success(`\n✅ Created ${prdResult.tasks.length} tasks linked to PRD ${prdResult.id}`));
        
        // 显示 AI 模式信息
        if (options.aiMode !== 'manual') {
          console.log(colors.info(`\n🤖 AI Mode: ${options.aiMode}`));
          if (options.enableMemory) {
            console.log(colors.info('   Memory system enabled for intelligent suggestions'));
          }
          const hasAgentAssignments = prdResult.tasks.some(t => t.agentAssignments?.primary);
          if (hasAgentAssignments) {
            console.log(colors.info('   Agents automatically assigned to tasks'));
          }
        }
      }
      
      // 如果不需要生成任务，到此结束
      if (options.noTasks) {
        console.log(colors.header('\n📌 Next Steps:\n'));
        console.log('1. Review and edit the PRD: ' + colors.command(`code ${prdPath}`));
        console.log('2. Parse PRD to create tasks: ' + colors.command(`workflow parse-prd ${prdPath}`));
        return;
      }
      
      // 生成设计文档（如果需要）
      if (options.withDesign) {
        console.log(colors.dim('\n🎨 Generating design document...'));
        
        const designDocManager = new DesignDocManager();
        const { doc: designDoc, content } = await designDocManager.generateFromPRD(prdPath, {
          template: options.designTemplate,
          author: process.env.USER || 'architect',
        });
        
        console.log(colors.success(`\n✅ Design document saved to: ${designDoc.path}`));
        
        // 如果设计文档创建了任务，显示信息
        if (designDoc.taskIds && designDoc.taskIds.length > 0) {
          console.log(colors.info(`   Created ${designDoc.taskIds.length} technical tasks from design`));
        }
      }
      
      // 显示完成信息
      console.log(colors.header('\n🎉 Workflow Created Successfully!\n'));
      console.log(colors.info('From idea to actionable tasks in seconds!'));
      
      // 后续步骤
      console.log(colors.header('\n📌 Next Steps:\n'));
      console.log('1. Review PRD: ' + colors.command(`code ${prdPath}`));
      console.log('2. View tasks: ' + colors.command('workflow task list'));
      console.log('3. Start working: ' + colors.command('workflow next'));
      console.log('4. Or use AI: ' + colors.command('workflow orchestrate start'));
      if (!options.withDesign) {
        console.log('5. Generate design doc: ' + colors.command(`workflow design generate ${prdPath}`));
      }
      
      // 大项目警告（基于任务估时）
      if (prdResult.tasks && prdResult.tasks.length > 0) {
        const totalHours = prdResult.tasks.reduce((sum, task) => {
          if (task.estimate) {
            const match = task.estimate.match(/(\d+)([hd])/);
            if (match) {
              const value = parseInt(match[1]);
              const unit = match[2];
              return sum + (unit === 'd' ? value * 8 : value);
            }
          }
          return sum;
        }, 0);
        
        if (totalHours > 40) {
          console.log(colors.warning(`\n⚠️  Large project detected (${totalHours}h estimated)`));
          console.log('Consider breaking it down into phases or milestones.');
        }
      }
    }));
  
  return command;
}